import { useMetadataUser } from 'contexts/metadata-user-context'
import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { SharedTypes } from 'services/get-profile'
import {
  Root as DtoBodyUpdateAddress,
  updateCompanyAddress,
} from 'services/update-company-address'
import { Formatter } from 'utils/formatters'

import { MainAccountLayout } from '../main-layout'
import { Dropzone } from './Dropzone'
import { validationSchemaUpdateCompanyAddress } from './schemas'

export const CompanyInfo = () => {
  const { profile } = useMetadataUser()
  const [enableUploadFile, setEnableUploadFile] = useState(false)
  const [clearUploadEvent, setClearUploadEvent] = useState(false)

  const registeredInTheDatabase = {
    city: profile?.company.address.city,
    complement: profile?.company.address.complement,
    neighborhood: profile?.company.address.neighborhood,
    state: profile?.company.address.state,
    streetName: profile?.company.address.street_name,
    streetNumber: profile?.company.address.street_number,
  }

  const formik = useFormik<UpdateCompanyAddressFormValues>({
    initialValues: {
      city: '',
      complement: '',
      neighborhood: '',
      state: '',
      streetName: '',
      streetNumber: '',
    },
    validationSchema: validationSchemaUpdateCompanyAddress,
    onSubmit: (values) => {
      const body: DtoBodyUpdateAddress = {
        city: values.city,
        complement: values.complement,
        neighborhood: values.neighborhood,
        state: values.state,
        street_name: values.streetName,
        street_number: values.streetNumber,
      }

      if (
        JSON.stringify(formik.values) ===
        JSON.stringify(registeredInTheDatabase)
      ) {
        toast.success('Dados atualizados com sucesso', {
          duration: 2000,
        })

        setTimeout(() => {
          window.location.reload()
        }, 2000)
        return
      }

      toast
        .promise(updateCompanyAddress(body), {
          loading: 'Salvando...',
          success: 'Dados atualizados com sucesso',
          error: 'Erro ao atualizar dados',
        })
        .then(() => {
          setTimeout(() => {
            window.location.reload()
          }, 2000)
        })
    },
  })

  useEffect(() => {
    if (profile) {
      formik.setValues({
        city: profile.company.address.city,
        complement: profile.company.address.complement,
        neighborhood: profile.company.address.neighborhood,
        state: profile.company.address.state,
        streetName: profile.company.address.street_name,
        streetNumber: profile.company.address.street_number,
      })
    }
  }, [profile])

  const isLoading = !profile

  const isEqualToTheOneReturnedFromTheBank =
    JSON.stringify(formik.values) === JSON.stringify(registeredInTheDatabase)

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100svh-15rem)]">
        <p>Carregando...</p>
      </div>
    )
  }

  const inputs_fields = [
    {
      label: 'Nome da empresa',
      placeholder: 'Fantasia',
      value: profile.company.trading_name,
    },
    {
      label: 'Razão social',
      placeholder: 'Fantasia LTDA',
      value: profile.company.name,
    },
    {
      label: 'CNPJ',
      placeholder: 'xx.xxx.xxx/xxxx-xx',
      value: Formatter.prettyCNPJ(profile.company.cnpj),
    },
    {
      label: 'Contrato',
      placeholder: 'URL',
      value: 'N/D',
    },
    {
      address: [
        {
          label: 'Rua',
          placeholder: 'Rua, número, bairro',
          name: 'streetName',
          value: profile.company.address.street_name,
        },
        {
          label: 'Numero',
          placeholder: 'xxx',
          name: 'streetNumber',
          value: profile.company.address.street_number,
        },
        {
          label: 'Bairro',
          placeholder: 'Bairro',
          name: 'neighborhood',
          value: profile.company.address.neighborhood,
        },
        {
          label: 'Cidade',
          placeholder: 'Cidade',
          name: 'city',
          value: profile.company.address.city,
        },
        {
          label: 'Estado',
          placeholder: 'Estado',
          name: 'state',
          value: profile.company.address.state,
        },
      ],
    },
    {
      label: 'Valor base da Coparticipação',
      placeholder: 'R$ XX,XX',
      value: `${profile.company.share_type === SharedTypes.FIXED ? `R$${profile.company.default_share_value}` : `${profile.company.default_share_value}%`}`,
    },
  ]

  const disabledSubmit = enableUploadFile
    ? false
    : isEqualToTheOneReturnedFromTheBank

  const handleError = <T extends UpdateCompanyAddressFormValues>(
    fieldName: keyof T,
    formik: any,
  ) => {
    if (formik.touched[fieldName] && Boolean(formik.errors[fieldName])) {
      return `mt-1 border border-red-500 focus:ring-2 focus:outline-none focus:ring-red-500 focus:border-red-500`
    }
    return `mt-1 border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`
  }

  const handleCancel = () => {
    formik.setValues({
      city: profile.company.address.city,
      complement: profile.company.address.complement,
      neighborhood: profile.company.address.neighborhood,
      state: profile.company.address.state,
      streetName: profile.company.address.street_name,
      streetNumber: profile.company.address.street_number,
    })

    setClearUploadEvent(true)
  }

  return (
    <MainAccountLayout>
      <section className="w-full h-full flex flex-col justify-start items-start">
        <div className="flex flex-col w-full px-1 md:px-4">
          <h1 className="text-2xl font-bold mb-4">{profile.company.name}</h1>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

          <form className="w-full" onSubmit={formik.handleSubmit}>
            {inputs_fields.map((input, index) => (
              <div
                key={`${input.value}.${index}`}
                className={`w-full mt-8 ${
                  input.address
                    ? 'flex flex-row items-center gap-5 flex-wrap'
                    : 'md:w-1/3'
                }`}
              >
                {input.address ? (
                  input.address.map((address, index) => (
                    <div
                      key={`${address.value}.${index}`}
                      className="w-full md:w-1/4"
                    >
                      <div className="mb-4 ">
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                        >
                          {address.label}
                        </label>
                        <input
                          type="text"
                          placeholder={address.placeholder}
                          autoComplete="name"
                          value={
                            formik.values[
                              // eslint-disable-next-line max-len
                              address.name as keyof UpdateCompanyAddressFormValues
                            ]
                          }
                          defaultValue={address.value}
                          name={address.name}
                          id={address.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`${handleError(address.name as keyof UpdateCompanyAddressFormValues, formik)} block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="mb-4">
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                    >
                      {input.label}
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      placeholder={input.placeholder}
                      autoComplete="name"
                      readOnly
                      value={input.value}
                      className={`block bg-neutral-100 dark:bg-neutral-800
                      w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                    />
                  </div>
                )}
              </div>
            ))}

            <div className="w-full mt-8">
              <div className="mb-4">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-300 mb-2"
                >
                  Logo
                </label>
                <Dropzone
                  setTriggersUploadEvent={setEnableUploadFile}
                  triggersUploadEvent={formik.isSubmitting}
                  clearUploadEvent={clearUploadEvent}
                />
              </div>
            </div>

            <div className="flex flex-col w-full md:flex-row md:w-1/2 gap-4 md:items-center mt-8">
              <button
                type="button"
                className={`
                bg-white border-2 dark:bg-neutral-700
                hover:dark:bg-neutral-700/60 transition-colors duration-500
                ease-in-out text-neutral-900 dark:text-neutral-200 px-6
                py-2 rounded-full disabled:cursor-not-allowed disabled:bg-neutral-200
                disabled:hover:bg-neutral-200 dark:disabled:bg-neutral-700
                dark:disabled:hover:bg-neutral-700
              `}
                onClick={handleCancel}
                disabled={formik.isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={`
                bg-[#4F46E5] hover:bg-[#4F46E5]/80 hover:dark:bg-[#4F46E5]/60
                transition-colors duration-500 ease-in-out text-white
                px-6 py-2 rounded-full disabled:cursor-not-allowed disabled:bg-[#8580d8]
                disabled:hover:bg-[#8e89dd] dark:disabled:bg-[#4F46E5]
              `}
                disabled={formik.isSubmitting || disabledSubmit}
              >
                Atualizar
              </button>
            </div>
          </form>
        </div>
      </section>
    </MainAccountLayout>
  )
}
